import { FIREBASE_PATHS } from '../config/firebase';
import type { RealtimeDatabasePort, RemoteChild } from './firebase';

export const CHAT_HISTORY_PAGE_SIZE = 15;
const CHAT_HISTORY_QUERY_SIZE = CHAT_HISTORY_PAGE_SIZE + 1;
export const CHAT_NAME_MAX_LENGTH = 20;
export const CHAT_MESSAGE_MAX_LENGTH = 280;
export const CHAT_SEND_COOLDOWN_MS = 1_200;

export type ChatConnectionState = 'idle' | 'loading' | 'connected' | 'offline' | 'error';

export interface ChatMessage {
  readonly id: string;
  readonly uid: string;
  readonly name: string;
  readonly text: string;
  readonly timestamp: number;
}

export interface ChatDateGroup {
  readonly dateKey: string;
  readonly label: string;
  readonly messages: readonly ChatMessage[];
}

export interface ChatSnapshot {
  readonly state: ChatConnectionState;
  readonly messages: readonly ChatMessage[];
  readonly hasMoreHistory: boolean;
  readonly error?: string;
}

export type ChatListener = (snapshot: ChatSnapshot) => void;

export interface ChatServicePort {
  readonly snapshot: ChatSnapshot;
  open(listener: ChatListener): Promise<ChatSnapshot>;
  close(): void;
  loadOlder(): Promise<readonly ChatMessage[]>;
  send(displayName: unknown, messageText: unknown): Promise<string>;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

export function sanitizeChatName(value: unknown): string {
  if (typeof value !== 'string') return 'Duelist';
  return value.replace(/\s+/g, ' ').trim().slice(0, CHAT_NAME_MAX_LENGTH) || 'Duelist';
}

export function sanitizeChatText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\r/g, '').trim().slice(0, CHAT_MESSAGE_MAX_LENGTH);
}

function decodeChatMessage(child: RemoteChild): ChatMessage | null {
  const source = record(child.value);
  if (!source || typeof source.uid !== 'string' || !source.uid || typeof source.ts !== 'number'
    || !Number.isFinite(source.ts) || source.ts <= 0) return null;
  const decodedText = sanitizeChatText(source.text);
  if (!decodedText) return null;
  return Object.freeze({
    id: child.key, uid: source.uid, name: sanitizeChatName(source.name), text: decodedText,
    timestamp: Math.round(source.ts),
  });
}

function sorted(messages: Iterable<ChatMessage>): readonly ChatMessage[] {
  return [...messages].sort((left, right) => left.timestamp - right.timestamp || left.id.localeCompare(right.id));
}

export function groupChatMessages(
  messages: readonly ChatMessage[],
  locale = 'de-DE',
  timeZone?: string,
): readonly ChatDateGroup[] {
  const keyFormatter = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone });
  const labelFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone });
  const groups = new Map<string, ChatMessage[]>();
  for (const message of sorted(messages)) {
    const date = new Date(message.timestamp);
    const key = keyFormatter.format(date);
    const group = groups.get(key) ?? [];
    group.push(message);
    groups.set(key, group);
  }
  return [...groups].map(([dateKey, entries]) => Object.freeze({
    dateKey, label: labelFormatter.format(new Date(entries[0]!.timestamp)), messages: Object.freeze(entries),
  }));
}

export class ChatService implements ChatServicePort {
  private state: ChatConnectionState = 'idle';
  private messages = new Map<string, ChatMessage>();
  private hasMore = false;
  private unsubscribe: (() => void) | null = null;
  private listener: ChatListener | null = null;
  private lockedUntil = 0;
  private oldestLoadedKey: string | null = null;
  private liveWindowInitialized = false;

  constructor(
    private readonly database: RealtimeDatabasePort,
    private readonly now: () => number = Date.now,
  ) {}

  get snapshot(): ChatSnapshot {
    return Object.freeze({ state: this.state, messages: sorted(this.messages.values()), hasMoreHistory: this.hasMore });
  }

  /** Firebase is deliberately untouched until this method is called by the open-chat action. */
  async open(listener: ChatListener): Promise<ChatSnapshot> {
    this.listener = listener;
    if (this.unsubscribe) { listener(this.snapshot); return this.snapshot; }
    this.messages.clear();
    this.hasMore = false;
    this.oldestLoadedKey = null;
    this.liveWindowInitialized = false;
    this.state = 'loading';
    this.emit();
    const availability = await this.database.connect();
    if (availability.status !== 'online') {
      this.state = 'offline';
      this.emit(availability.reason);
      return this.snapshot;
    }
    this.unsubscribe = this.database.subscribeChildren(
      FIREBASE_PATHS.chatMessages,
      { orderByKey: true, limitToLast: CHAT_HISTORY_QUERY_SIZE },
      (children) => {
        const page = children.length > CHAT_HISTORY_PAGE_SIZE
          ? children.slice(-CHAT_HISTORY_PAGE_SIZE)
          : children;
        if (!this.liveWindowInitialized) {
          this.messages.clear();
          this.hasMore = children.length > CHAT_HISTORY_PAGE_SIZE;
          this.oldestLoadedKey = page[0]?.key ?? null;
          this.liveWindowInitialized = true;
        }
        for (const child of page) {
          const message = decodeChatMessage(child);
          if (message) this.messages.set(message.id, message);
        }
        this.state = 'connected';
        this.emit();
      },
      (reason) => { this.state = 'error'; this.emit(reason instanceof Error ? reason.message : 'Chat connection failed.'); },
    );
    return this.snapshot;
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.listener = null;
    this.state = 'idle';
  }

  async loadOlder(): Promise<readonly ChatMessage[]> {
    if (this.state !== 'connected' || !this.hasMore || this.oldestLoadedKey === null) return [];
    const children = await this.database.readChildren(FIREBASE_PATHS.chatMessages, {
      orderByKey: true,
      endBefore: this.oldestLoadedKey,
      limitToLast: CHAT_HISTORY_QUERY_SIZE,
    });
    const page = children.length > CHAT_HISTORY_PAGE_SIZE
      ? children.slice(-CHAT_HISTORY_PAGE_SIZE)
      : children;
    const added: ChatMessage[] = [];
    for (const child of page) {
      const message = decodeChatMessage(child);
      if (message && !this.messages.has(message.id)) { this.messages.set(message.id, message); added.push(message); }
    }
    this.oldestLoadedKey = page[0]?.key ?? this.oldestLoadedKey;
    this.hasMore = children.length > CHAT_HISTORY_PAGE_SIZE;
    return sorted(added);
  }

  async send(displayName: unknown, messageText: unknown): Promise<string> {
    if (this.state !== 'connected' || this.database.availability.status !== 'online' || !this.database.availability.uid) {
      throw new Error('Chat is offline.');
    }
    const timestamp = this.now();
    if (timestamp < this.lockedUntil) throw new Error('Chat cooldown is active.');
    const text = sanitizeChatText(messageText);
    if (!text) throw new TypeError('Chat message is empty.');
    this.lockedUntil = timestamp + CHAT_SEND_COOLDOWN_MS;
    return this.database.append(FIREBASE_PATHS.chatMessages, {
      uid: this.database.availability.uid,
      name: sanitizeChatName(displayName),
      text,
      ts: this.database.serverTimestamp(),
    });
  }

  private emit(error?: string): void {
    this.listener?.(Object.freeze({
      state: this.state, messages: sorted(this.messages.values()), hasMoreHistory: this.hasMore,
      ...(error ? { error } : {}),
    }));
  }
}
